import React, { useEffect, useState, useRef } from "react";
import { Outlet, Link, NavLink, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowUpRight, MapPin, Phone, User, LogOut, ChevronDown } from "lucide-react";
import "./preview.css";
import PreviewTabBar from "./PreviewTabBar";
import PreviewWhatsAppButton from "./PreviewWhatsAppButton";
import LanguageSwitcher from "../../components/LanguageSwitcher";
import RouteSEO from "../../components/RouteSEO";
import { useAuth } from "../../context/AuthContext";

const PreviewNavbar = () => {
    const { t } = useTranslation();
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [openMenu, setOpenMenu] = useState(false);
    const wrap = useRef(null);

    useEffect(() => {
        const onClick = (e) => {
            if (wrap.current && !wrap.current.contains(e.target)) setOpenMenu(false);
        };
        document.addEventListener("mousedown", onClick);
        return () => document.removeEventListener("mousedown", onClick);
    }, []);

    return (
        <header className="border-b border-[var(--border)] bg-[var(--bg)] sticky top-0 z-40" data-testid="main-navbar">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-5 flex items-center justify-between gap-3">
                <Link to="/" className="flex items-center gap-2.5 shrink-0" data-testid="brand-logo">
                    <div className="w-9 h-9 rounded-xl bg-[var(--ink)] text-white flex items-center justify-center">
                        <MapPin className="w-4 h-4" strokeWidth={2.5} />
                    </div>
                    <div className="leading-tight">
                        <div className="font-hz-display text-lg sm:text-xl font-semibold tracking-tight">SouthTrip</div>
                        <div className="text-[9px] sm:text-[10px] text-[var(--ink-softer)] tracking-wider uppercase">by LuxSaCabs</div>
                    </div>
                </Link>
                <nav className="hidden lg:flex items-center gap-1">
                    {[
                        { to: "/", label: t("nav.home"), end: true, key: "home" },
                        { to: "/cab-booking", label: t("nav.cabs"), key: "cab-booking" },
                        { to: "/airport-transfers", label: t("nav.transfers"), key: "airport-transfers" },
                        { to: "/tour-packages", label: t("nav.packages"), key: "tour-packages" },
                        { to: "/destinations", label: t("nav.destinations"), key: "destinations" },
                        { to: "/contact", label: t("nav.contact"), key: "contact" },
                    ].map((l) => (
                        <NavLink
                            key={l.to}
                            to={l.to}
                            end={l.end}
                            className={({ isActive }) =>
                                `px-4 xl:px-5 py-2 text-sm font-medium rounded-full transition ${
                                    isActive
                                        ? "bg-[var(--ink)] text-white"
                                        : "text-[var(--ink-soft)] hover:text-[var(--ink)]"
                                }`
                            }
                            data-testid={`nav-${l.key}`}
                        >
                            {l.label}
                        </NavLink>
                    ))}
                </nav>
                <div className="flex items-center gap-2 sm:gap-3">
                    <LanguageSwitcher variant="ghost" />
                    <a
                        href="tel:+918075814740"
                        className="hidden md:inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-[var(--ink)] text-white text-xs font-semibold hover:bg-[var(--brand)] transition shadow-sm whitespace-nowrap"
                        data-testid="nav-phone"
                        aria-label="Call SouthTrip India: +91 80-75814740"
                    >
                        <Phone className="w-3.5 h-3.5" strokeWidth={2.5} />
                        +91 80-75814740
                    </a>
                    {user ? (
                        <div className="relative" ref={wrap}>
                            <button
                                onClick={() => setOpenMenu((v) => !v)}
                                className="hidden lg:flex items-center gap-2 px-3 py-2 rounded-full bg-[var(--bg-surface)] hover:bg-[var(--border)] transition text-sm"
                                data-testid="user-menu-btn"
                            >
                                <span className="w-7 h-7 rounded-full bg-[var(--ink)] text-white flex items-center justify-center text-xs font-semibold">
                                    {user.name?.[0]?.toUpperCase() || "U"}
                                </span>
                                <span className="font-medium max-w-[110px] truncate">{user.name}</span>
                                <ChevronDown className="w-3.5 h-3.5 opacity-70" />
                            </button>
                            {openMenu && (
                                <div className="absolute right-0 top-full mt-2 w-52 bg-white border border-[var(--border)] rounded-xl shadow-lg p-1 z-50">
                                    <Link
                                        to="/my-bookings"
                                        onClick={() => setOpenMenu(false)}
                                        className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-[var(--bg-surface)] rounded-md"
                                        data-testid="user-my-bookings"
                                    >
                                        <User className="w-4 h-4" /> {t("nav.myBookings")}
                                    </Link>
                                    <button
                                        onClick={() => {
                                            logout();
                                            setOpenMenu(false);
                                            navigate("/");
                                        }}
                                        className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-[var(--bg-surface)] rounded-md"
                                        data-testid="user-logout-btn"
                                    >
                                        <LogOut className="w-4 h-4" /> {t("nav.logout")}
                                    </button>
                                </div>
                            )}
                        </div>
                    ) : (
                        <Link
                            to="/login"
                            className="hidden lg:inline-flex hz-btn-primary text-xs sm:text-sm py-2 px-4 sm:px-6"
                            data-testid="nav-login"
                        >
                            {t("nav.login")} <ArrowUpRight className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                        </Link>
                    )}
                </div>
            </div>
        </header>
    );
};

const PreviewFooter = () => {
    const { t } = useTranslation();
    return (
        <footer className="mt-24 lg:mt-32 bg-[var(--ink)] text-white hz-noise" data-testid="footer">
            <div className="max-w-7xl mx-auto px-6 py-16 lg:py-20">
                <div className="flex flex-col md:flex-row items-start md:items-end justify-between gap-10">
                    <div className="max-w-xl">
                        <div className="hz-chip-brand" style={{ background: "rgba(255,255,255,0.1)", color: "#fff" }}>
                            SouthTrip · LuxSaCabs
                        </div>
                        <h3 className="font-hz-display text-3xl sm:text-4xl md:text-6xl mt-6 tracking-tight leading-[0.95]">
                            {t("footer.promise")}
                        </h3>
                        <p className="mt-6 text-white/65 text-sm sm:text-base leading-relaxed">
                            {t("footer.tagline")}
                        </p>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-x-8 sm:gap-x-12 gap-y-3 text-sm text-white/75">
                        <div className="flex flex-col gap-2">
                            <div className="text-[10px] uppercase tracking-widest text-white/40 mb-1">Explore</div>
                            <Link to="/" className="hover:text-white">{t("nav.home")}</Link>
                            <Link to="/cab-booking" className="hover:text-white">{t("nav.cabs")}</Link>
                            <Link to="/airport-transfers" className="hover:text-white">{t("nav.transfers")}</Link>
                            <Link to="/tour-packages" className="hover:text-white">{t("nav.packages")}</Link>
                            <Link to="/destinations" className="hover:text-white">{t("nav.destinations")}</Link>
                        </div>
                        <div className="flex flex-col gap-2">
                            <div className="text-[10px] uppercase tracking-widest text-white/40 mb-1">Company</div>
                            <Link to="/about" className="hover:text-white">{t("nav.about")}</Link>
                            <Link to="/contact" className="hover:text-white">{t("nav.contact")}</Link>
                            <a href="tel:+918075814740" className="hover:text-white">+91 80-75814740</a>
                            <a href="mailto:info@southtrip.in" className="hover:text-white">info@southtrip.in</a>
                            <Link to="/admin/login" className="hover:text-white opacity-50">{t("nav.admin")}</Link>
                        </div>
                        <div className="flex flex-col gap-2 col-span-2 md:col-span-1" data-testid="footer-legal-col">
                            <div className="text-[10px] uppercase tracking-widest text-white/40 mb-1">Legal</div>
                            <Link to="/privacy-policy" className="hover:text-white" data-testid="footer-privacy-link">Privacy Policy</Link>
                            <Link to="/terms" className="hover:text-white" data-testid="footer-terms-link">Terms of Service</Link>
                            <Link to="/refund-policy" className="hover:text-white" data-testid="footer-refund-link">Refund Policy</Link>
                        </div>
                    </div>
                </div>
                <div className="border-t border-white/10 mt-12 sm:mt-16 pt-6 flex flex-col sm:flex-row justify-between gap-3 text-xs text-white/45">
                    <span>© {new Date().getFullYear()} SouthTrip.in · by LuxSaCabs · {t("footer.rights")}</span>
                    <span>35 P/N, Kavarapparambu Road, Angamaly · Kerala 683572</span>
                </div>
            </div>
        </footer>
    );
};

const PreviewLayout = () => {
    useEffect(() => {
        const link = document.createElement("link");
        link.rel = "stylesheet";
        link.href =
            "https://fonts.googleapis.com/css2?family=Bricolage+Grotesque:opsz,wght@12..96,300;12..96,400;12..96,500;12..96,600;12..96,700&display=swap";
        link.dataset.hz = "fonts";
        if (!document.querySelector('link[data-hz="fonts"]')) document.head.appendChild(link);
    }, []);

    return (
        <div className="theme-horizon min-h-screen">
            <PreviewNavbar />
            <main className="pb-24 lg:pb-0">
                <RouteSEO />
                <Outlet />
            </main>
            <PreviewFooter />
            <PreviewTabBar />
            <PreviewWhatsAppButton />
        </div>
    );
};

export default PreviewLayout;
